// Channel intent URL builders.
//
// Single source of truth for every external share endpoint. Pure — no DOM,
// no React, no analytics. Components compose these via the helpers in
// `share.ts` rather than calling them directly.

export type ShareChannel = 'x' | 'telegram' | 'whatsapp' | 'reddit' | 'sms' | 'mailto';

export interface ShareContent {
  /** Canonical URL being shared. Required for every channel except the
   *  text-only X intent (which still benefits from a URL in the text). */
  url: string;
  /** Pre-composed share text. Channels that take a separate `text` field
   *  use it; otherwise it is appended to the URL by the channel. */
  text?: string;
  /** Subject line used by `mailto:` only. */
  subject?: string;
}

function enc(value: string): string {
  return encodeURIComponent(value);
}

export function xIntent(content: ShareContent): string {
  // X (Twitter) accepts text + url; if both are given X composes
  // "<text> <url>". When only text is provided the URL must already be
  // embedded in the text.
  const params: string[] = [];
  if (content.text !== undefined && content.text.length > 0) {
    params.push(`text=${enc(content.text)}`);
  }
  if (content.url.length > 0) {
    params.push(`url=${enc(content.url)}`);
  }
  return `https://twitter.com/intent/tweet?${params.join('&')}`;
}

export function telegramIntent(content: ShareContent): string {
  const text = content.text ?? '';
  return `https://t.me/share/url?url=${enc(content.url)}&text=${enc(text)}`;
}

export function whatsappIntent(content: ShareContent): string {
  // WhatsApp has a single `text` field; URL is appended only when given.
  const text = content.text ?? '';
  const body =
    content.url.length > 0 ? (text.length > 0 ? `${text} ${content.url}` : content.url) : text;
  return `https://api.whatsapp.com/send?text=${enc(body)}`;
}

export function redditIntent(content: ShareContent): string {
  const title = content.text ?? '';
  return `https://www.reddit.com/submit?url=${enc(content.url)}&title=${enc(title)}`;
}

export function smsIntent(content: ShareContent): string {
  const text = content.text ?? '';
  const body =
    content.url.length > 0 ? (text.length > 0 ? `${text} ${content.url}` : content.url) : text;
  return `sms:?body=${enc(body)}`;
}

export function mailtoIntent(content: ShareContent): string {
  const subject = content.subject ?? '';
  const text = content.text ?? '';
  const body =
    content.url.length > 0 ? (text.length > 0 ? `${text}\n\n${content.url}` : content.url) : text;
  return `mailto:?subject=${enc(subject)}&body=${enc(body)}`;
}

const BUILDERS: Record<ShareChannel, (content: ShareContent) => string> = {
  x: xIntent,
  telegram: telegramIntent,
  whatsapp: whatsappIntent,
  reddit: redditIntent,
  sms: smsIntent,
  mailto: mailtoIntent,
};

export function buildShareIntent(channel: ShareChannel, content: ShareContent): string {
  return BUILDERS[channel](content);
}
